import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  ListItem,
  Typography,
  List,
  Divider,
  Button,
  Avatar,
  Stack,
} from '@mui/material';
import {
  IMedicationHistory,
  IOnceIndOptions,
  IPrescriptionMedicationHistory,
} from 'API';
import { GridTitles, WarningWithExpiredDate } from 'Components';
import { shorterDateFormat, slashDateFormat } from 'Config';
import { format } from 'date-fns';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  AccordionArrowPurple,
  AccordionArrowPurpleClose,
  UploadFileBlue,
} from 'Images';
import { Fragment, useEffect, useState } from 'react';
import { goToRenewPrescription } from 'Utils';

interface IMedicationHistoryListProps {
  medicationHistory: IMedicationHistory;
  onceIndOptions: IOnceIndOptions[];
}

export const MedicationHistoryItem = (props: IMedicationHistoryListProps) => {
  const { medicationHistory } = props;
  const { onceIndOptions } = props;
  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicationsHistory);
  const medicationsAndPrescriptionsLobbyDictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );
  const isDesktop = useIsDesktop();

  const [onceIndText, setOnceIndText] = useState<string>();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const titles = [
    { text: dictionary.openDate, size: 2.25 },
    { text: dictionary.prescriptionNumber, size: 1.75 },
    { text: dictionary.dosage, size: 3 },
    { text: dictionary.note, size: 3 },
    { text: dictionary.status, size: 2 },
  ];

  const gridLabels = [
    dictionary.medicineName,
    dictionary.frequency,
    dictionary.validity,
  ];

  const getAndSetOnceIndText = () => {
    if (medicationHistory.onceInd) {
      const opt = onceIndOptions.find(
        (element: IOnceIndOptions) => medicationHistory.onceInd === element.code
      );
      setOnceIndText(opt?.text);
    } else {
      setOnceIndText(dictionary.constMedication);
    }
  };

  const getStatusLabel = (
    medicationPrescription: IPrescriptionMedicationHistory
  ) => {
    const statuses = medicationPrescription?.statuses;
    if (statuses) {
      const status = statuses?.find(
        (status) => status.calcStatus === medicationPrescription.currentStatus
      );
      if (status) {
        return status.label;
      }
    }
    return '';
  };

  useEffect(() => {
    getAndSetOnceIndText();
  }, []);

  return isDesktop ? (
    <ListItem
      sx={{
        p: 0,
        mb: 2,
      }}
    >
      <Accordion
        TransitionProps={{ timeout: 400 }}
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          width: '100%',
          boxShadow: 'unset',
          borderRadius: '16px !important',
          border: '1px solid',
          borderColor: 'primary.shade_200',
        }}
        disableGutters
      >
        <AccordionSummary
          sx={{
            pt: 3,
            pb: '21px',
            pr: 3,
            pl: 3,
            borderRadius: isExpanded ? '16px 16px 0 0' : '16px',
            '& .MuiAccordionSummary-content': {
              margin: '0',
            },

            '&.Mui-focusVisible': {
              backgroundColor: '#fff',
            },
          }}
          expandIcon={
            <Avatar
              tabIndex={0}
              sx={
                isExpanded
                  ? {
                      width: isDesktop ? '36px' : '24px',
                      height: isDesktop ? '36px' : '24px',
                      transform: 'rotate(180deg)',
                    }
                  : {
                      width: isDesktop ? '40px' : '24px',
                      height: isDesktop ? '40px' : '24px',
                      transform: 'rotate(0deg)',
                    }
              }
              src={
                isExpanded ? AccordionArrowPurpleClose : AccordionArrowPurple
              }
              alt=''
            />
          }
        >
          <Grid container>
            <Grid item xs={12} md={4}>
              <Stack>
                <Typography
                  variant='body6'
                  sx={{ mr: isDesktop ? undefined : '6px' }}
                >
                  {gridLabels[0]}
                </Typography>
                <Typography>{medicationHistory.medicationName}</Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack>
                <Typography
                  variant='body6'
                  sx={{ mr: isDesktop ? undefined : '6px' }}
                >
                  {gridLabels[1]}
                </Typography>
                <Typography>{onceIndText}</Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={3}>
              <Stack>
                <Typography
                  variant='body6'
                  sx={{ mr: isDesktop ? undefined : '6px' }}
                >
                  {gridLabels[2]}
                </Typography>
                <Typography>
                  {format(
                    new Date(medicationHistory.nextRefreshDate),
                    shorterDateFormat
                  )}
                </Typography>
              </Stack>
            </Grid>
            <WarningWithExpiredDate
              xs={6}
              date={medicationHistory.nextRefreshDate}
            />
          </Grid>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            pt: 5,
            pb: 5,
          }}
        >
          {medicationHistory.prescriptions.length > 0 && (
            <GridTitles titles={titles} ml={1} />
          )}
          <List sx={{ p: 0 }}>
            {medicationHistory.prescriptions.map((prescription) => (
              <Fragment key={prescription.code}>
                <ListItem
                  sx={{
                    pr: 0,
                    pl: 0,
                    pb: '12px',
                    pt: '24px',
                    ml: 1,
                    borderBottom: '1px solid',
                    borderColor: 'primary.shade_100',
                  }}
                >
                  <Grid container>
                    <Grid container item xs={titles[0].size}>
                      <Typography variant='body4'>
                        {format(
                          new Date(prescription.ratificationDate),
                          slashDateFormat
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={titles[1].size}>
                      <Typography variant='body6'>
                        {prescription.code}
                      </Typography>
                    </Grid>
                    <Grid item xs={titles[2].size}>
                      <Typography variant='body6'>
                        {medicationsAndPrescriptionsLobbyDictionary.dosageDetails(
                          prescription.dosage,
                          prescription.doseperday,
                          prescription.fornumberdays
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={titles[3].size}>
                      <Typography variant='body6'>
                        {prescription.medremark}
                      </Typography>
                    </Grid>
                    <Grid item xs={titles[4].size}>
                      <Typography variant='body6'>
                        {getStatusLabel(prescription)}
                      </Typography>
                    </Grid>
                  </Grid>
                </ListItem>
              </Fragment>
            ))}
          </List>
          <Grid mt={4} gap={3} container justifyContent='flex-end'>
            <Button
              onClick={() => goToRenewPrescription('History')}
              variant='outlined'
              sx={{ width: '300px', height: '46px' }}
              startIcon={
                <Avatar
                  alt=''
                  src={UploadFileBlue}
                  sx={{ width: '20px', height: '20px' }}
                />
              }
            >
              <Typography fontSize={18}>{dictionary.attachDocument}</Typography>
            </Button>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </ListItem>
  ) : (
    /** MOBILE History Item **/
    <ListItem sx={{ p: 0, mb: 2 }}>
      <Accordion
        TransitionProps={{ timeout: 400 }}
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          width: '100%',
          boxShadow: 'unset',
          borderRadius: '16px !important',
          border: '1px solid',
          borderColor: 'primary.shade_200',
        }}
        disableGutters
      >
        <AccordionSummary
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            p: '24px',
            boxShadow: isExpanded
              ? '0px 4px 28px rgba(45, 112, 217, 0.15)'
              : '',
            borderRadius: isExpanded ? '16px 16px 0 0' : '16px',
            '& .MuiAccordionSummary-expandIconWrapper': {
              alignSelf: 'start',
              mt: '-5px',
            },
            '& .MuiAccordionSummary-content': {
              margin: '0',
            },
          }}
          expandIcon={
            <Avatar
              tabIndex={0}
              sx={
                isExpanded
                  ? {
                      width: isDesktop ? '36px' : '24px',
                      height: isDesktop ? '36px' : '24px',
                      transform: 'rotate(180deg)',
                    }
                  : {
                      width: isDesktop ? '40px' : '24px',
                      height: isDesktop ? '40px' : '24px',
                      transform: 'rotate(0deg)',
                    }
              }
              src={
                isExpanded ? AccordionArrowPurpleClose : AccordionArrowPurple
              }
            />
          }
        >
          <Grid container>
            <Grid item xs={12} mb={2}>
              <Stack>
                <Typography
                  variant='body6'
                  sx={{ mr: isDesktop ? undefined : '6px' }}
                >
                  {gridLabels[0]}
                </Typography>
                <Typography variant='body2'>
                  {medicationHistory.medicationName}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={6}>
              <Stack>
                <Typography
                  variant='body6'
                  sx={{ mr: isDesktop ? undefined : '6px' }}
                >
                  {gridLabels[1]}
                </Typography>
                <Typography variant='body2'>{onceIndText}</Typography>
                <WarningWithExpiredDate
                  xs={6}
                  date={medicationHistory.nextRefreshDate}
                />
              </Stack>
            </Grid>

            <Grid item xs={6}>
              <Stack>
                <Typography
                  variant='body6'
                  sx={{ mr: isDesktop ? undefined : '6px' }}
                >
                  {gridLabels[2]}
                </Typography>
                <Typography variant='body2'>
                  {format(
                    new Date(medicationHistory.nextRefreshDate),
                    shorterDateFormat
                  )}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <List
            sx={
              medicationHistory.prescriptions.length
                ? {
                    p: 3,
                  }
                : { p: 0 }
            }
          >
            {medicationHistory.prescriptions.map((prescription, index) => (
              <Fragment key={prescription.code}>
                <ListItem sx={{ p: 0 }}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant='body6'>
                        {dictionary.openDate}
                      </Typography>
                      <Typography>
                        {format(
                          new Date(prescription.createDate),
                          shorterDateFormat
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant='body6'>
                        {dictionary.prescriptionNumber}
                      </Typography>
                      <Typography>{prescription.code}</Typography>
                    </Grid>
                    <Grid item xs={12} mt={'5px'}>
                      <Typography variant='body6'>
                        {dictionary.dosage}
                      </Typography>
                      <Typography variant='body2' mb={'5px'}>
                        {dictionary.dosageFullDescription(
                          prescription.dosage,
                          prescription.doseperday,
                          prescription.fornumberdays
                        )}
                      </Typography>
                    </Grid>
                    {prescription.medremark && (
                      <Grid item xs={6}>
                        <Typography variant='body6'>
                          {dictionary.note}
                        </Typography>
                        <Typography variant='body2'>
                          {prescription.medremark}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item xs={6}>
                      {getStatusLabel(prescription) && (
                        <>
                          <Typography variant='body6'>
                            {dictionary.status}
                          </Typography>
                          <Typography variant='body2'>
                            {getStatusLabel(prescription)}
                          </Typography>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </ListItem>
                {index < medicationHistory.prescriptions.length - 1 && (
                  <Divider
                    aria-hidden='true'
                    sx={{ marginY: '24px', borderColor: 'primary.shade_200' }}
                  />
                )}
              </Fragment>
            ))}
          </List>
          <Grid container justifyContent='center' p={3}>
            {/* Comment this for now until this feature will be available */}
            {/* <Button variant='outlined'>{dictionary.deleteFromList}</Button> */}
            <Button
              onClick={() => goToRenewPrescription('History')}
              variant='outlined'
              sx={{
                width: '100%',
                height: '46px',
                boxShadow: '0px 2px 16px 0px rgba(4, 16, 46, 0.08)',
              }}
              startIcon={
                <Avatar
                  alt=''
                  src={UploadFileBlue}
                  sx={{ width: '20px', height: '20px' }}
                />
              }
            >
              <Typography fontSize={18}>{dictionary.attachDocument}</Typography>
            </Button>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
};
